import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
    UrlTree
} from '@angular/router';
import {
    FlyFreelyLoggingService,
    hasFeatureFlag
} from '@flyfreely-portal-ui/flyfreely';
import {
    LoadingOrganisations,
    SelectedOrganisation,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

/**
 * This guard checks that the organisation has the feature flag defined as `requiredFeatureFlag` in the route data.
 *
 * You often need to add `runGuardsAndResolvers: 'always'` to the router configuration so the change of
 * organisation is detected.
 *
 * The guard triggers org loading itself because WorkspaceComponent processes query params
 * only after navigation succeeds (after guards run). For system admins accessing orgs
 * outside their list, this ensures the async org fetch happens before the feature flag check.
 */
@Injectable()
export class FeatureFlagGuard {
    constructor(
        private workspaceStateService: WorkspaceStateService,
        private logger: FlyFreelyLoggingService,
        private router: Router
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> {
        const featureFlag = route.data.requiredFeatureFlag;
        if (featureFlag == null) {
            return of(true);
        }

        if (route.queryParams.organisationId == null) {
            this.logger.error('No current organisationId');
            return of(false);
        }

        const orgId = parseInt(route.queryParams.organisationId);

        // Trigger org loading — WorkspaceComponent processes query params AFTER guards run,
        // so we must initiate the org change here to avoid a race condition.
        this.workspaceStateService.changeOrganisation(orgId);

        return this.workspaceStateService.currentOrganisation$.pipe(
            filter(
                (
                    org
                ): org is Exclude<SelectedOrganisation, LoadingOrganisations> =>
                    org.type !== 'loading'
            ),
            take(1),
            map(org => {
                if (
                    org.type === 'no_access' ||
                    org.type === 'no_organisation'
                ) {
                    return this.router.createUrlTree(['/']);
                }
                // organisation_loaded or no_licence — both have an `organisation` field
                return hasFeatureFlag(org.organisation, featureFlag)
                    ? true
                    : this.router.createUrlTree(['/home'], {
                          queryParams: route.queryParams
                      });
            })
        );
    }
}
